import { useEffect, useRef, useState } from "react";
import { geocodeSuggest, GeocodeItem } from "../lib/publicApi";

type Props = {
  value: string;
  onChange: (v: string) => void;
  onSelect: (item: GeocodeItem) => void;
  placeholder?: string;
  className?: string;
  id?: string;
};

/**
 * Input s Mapy.cz našeptávačem. Vybráním dropdown položky získáš
 * strukturovanou adresu + lat/lon — ideální pro výpočet vzdálenosti.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  className,
  id,
}: Props) {
  const [items, setItems] = useState<GeocodeItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Fetch s debounce 300ms
  useEffect(() => {
    if (value.trim().length < 3) {
      setItems([]);
      return;
    }
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const res = await geocodeSuggest(value);
        if (!ctrl.signal.aborted) setItems(res);
      } catch {
        // tiché — autocomplete chyby uživatele neobtěžují
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  // Click outside → zavřít
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        id={id}
        type="text"
        className={className ?? "input"}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder ?? "Začni psát adresu školky…"}
        autoComplete="off"
      />

      {open && items.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {items.map((it, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => {
                  onSelect(it);
                  setOpen(false);
                }}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-obloha-indigo-50"
              >
                <div className="font-medium text-slate-900">{it.label}</div>
                {(it.city || it.zip) && (
                  <div className="text-xs text-slate-500">
                    {[it.zip, it.city].filter(Boolean).join(" ")}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
          ⏳
        </div>
      )}
    </div>
  );
}
